import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { CompanyLogo } from "@/components/common";
import { Section } from "@/components/oversikt/bits";
import { K } from "@/components/canvas/kalkyle/SummarySections";
import { ago } from "@/lib/sk";

const first = (n) => (n || "").split(" ")[0];

/** One version waiting for a decision – who asks, how much, what to look at – and the one way in: the control surface itself. */
function Row({ r, mine }) {
  const to = `/p/${r.case_id}?tab=tilbud&kontroll=1`;
  return (
    <article className="group relative -mx-4 flex items-start gap-4 rounded-[16px] px-4 py-4 transition-colors hover:bg-black/[0.025]" data-testid={`kontroll-row-${r.number}`}>
      <CompanyLogo name={r.customer} size="md" className="mt-0.5 h-9 w-9 shrink-0 rounded-[10px] p-1" />
      <div className="min-w-0 flex-1">
        <Link to={to} className="block truncate text-[16px] font-semibold tracking-[-0.012em] text-foreground after:absolute after:inset-0 after:rounded-[16px]" data-testid={`kontroll-row-${r.number}-title`}>{r.name}</Link>
        <div className="mt-0.5 truncate text-[13.5px] text-muted-foreground">{[r.customer, r.vessel && r.vessel !== r.name ? r.vessel : null, `#${r.number}`].filter(Boolean).join(" · ")}</div>
        <div className="mt-2 text-[14px] leading-[1.55]">
          <span className="font-medium text-foreground">Versjon {r.revision}{r.sum != null ? ` · ${K(r.sum)}` : ""}</span>
          <span className="text-foreground/65"> · {mine ? `hos ${(r.to || []).map((p) => first(p.name)).join(", ") || "kontrollør"}` : `fra ${first(r.by?.name)}`} · {ago(r.at)}</span>
        </div>
        {r.note ? <p className="mt-1 text-[14px] leading-[1.5] text-foreground/80" data-testid={`kontroll-row-${r.number}-note`}>«{r.note}»</p> : null}
      </div>
      <Link to={to} className="relative z-10 mt-1 hidden h-9 shrink-0 items-center gap-1.5 rounded-full bg-[hsl(var(--navy))] px-3.5 text-[13.5px] font-semibold text-white transition-colors hover:bg-[hsl(var(--ink-2))] sm:inline-flex" data-testid={`kontroll-row-${r.number}-open`}>{mine ? "Åpne" : "Kontroller"} <ArrowRight className="h-4 w-4" /></Link>
    </article>
  );
}

/**
 * Hjem for the controller: the versions that wait for *my* decision come first, as the most pressing thing on the page –
 * and for the one who sent them, a quiet line that says where they are.
 */
export function KontrollBlock({ kontroll }) {
  const mine = kontroll?.for_me || [];
  const waiting = kontroll?.waiting || [];
  if (!mine.length && !waiting.length) return null;
  return (
    <>
      {mine.length ? (
        <Section title={<span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Venter på din kontroll</span>} count={mine.length} intro={`${mine.length === 1 ? "Én versjon" : `${mine.length} versjoner`} er sendt til deg for intern kontroll. Godkjenning her er fire øyne – det sender ingenting til kunden.`} testId="oversikt-kontroll">
          <div className="space-y-1">{mine.map((r) => <Row key={r.quote_id} r={r} />)}</div>
        </Section>
      ) : null}
      {waiting.length ? (
        <Section title="Sendt til kontroll" count={waiting.length} testId="oversikt-kontroll-waiting">
          <div className="space-y-1">{waiting.map((r) => <Row key={r.quote_id} r={r} mine />)}</div>
        </Section>
      ) : null}
    </>
  );
}
